import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { overrideCompanyNames } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  isLeidosUser,
  isTerminatedCustomer,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { SubmissionError } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/addNewClientModal/AddNewClientModal'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { useAddNewClientMutation, useGetProviderProgramsQuery } from '../../../../../addNewClients/data/addNewClientApi'
import { customerExclusionList } from '../../../../../common/constants/appConstants'
import { getRequestPaymentCustomersForDisplay } from '../../../../../data/requestPayment/requestPaymentSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { setToastContent } from '../../../../data/ltToastAutoActions'

export const useAddNewClientModals = (userId: string) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const customers = useSelector(getRequestPaymentCustomersForDisplay)

  const [addNewClient, { isLoading: isAddingNewClient }] = useAddNewClientMutation()

  const [showAddNewClientModal, setShowAddNewClientModal] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [addedClientFirstName, setAddedClientFirstName] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null)

  const { data: programs, isFetching: isFetchingPrograms } = useGetProviderProgramsQuery(
    { customer: selectedCompany ?? '', providerLyraId: userId },
    { skip: !selectedCompany },
  )

  const careTypeOptions = useMemo(
    () =>
      selectedCompany
        ? (programs ?? []).map((programName) => ({
            label: getProgramDisplayLabel(intl, programName, 'daClientList'),
            value: programName,
          }))
        : [],
    [programs, intl, selectedCompany],
  )

  const companies = useMemo(
    () =>
      customers
        ?.filter(
          (company: CustomerInfo) =>
            !customerExclusionList.includes(company.value) &&
            !isLeidosUser(company.value) &&
            !isTerminatedCustomer(company),
        )
        .map((company) => {
          const value = company.value as keyof typeof overrideCompanyNames
          return value in overrideCompanyNames ? { ...company, label: overrideCompanyNames[value] } : company
        }),
    [customers],
  )

  const customerAliases = useMemo(() => {
    const aliases: CustomerInfo[] = []
    companies?.forEach((company) => {
      company.customer_aliases?.forEach((customerAlias: string) => {
        aliases.push({ ...company, label: customerAlias })
      })
    })
    return aliases
  }, [companies])

  const allCustomers = useMemo(() => [...companies, ...customerAliases], [companies, customerAliases])

  const openAddClientModal = useCallback(() => {
    track({ event: mixpanelEvents.ADD_NEW_CLIENT_OPEN_MODAL })
    setAddedClientFirstName(null)
    setSelectedCompany(null)
    setSubmissionError(null)
    setShowAddNewClientModal(true)
  }, [])

  const openTransitionModal = useCallback((company: string | null) => {
    setAddedClientFirstName(null)
    setSelectedCompany(company)
    setSubmissionError(null)
    setShowAddNewClientModal(true)
  }, [])

  const closeAddClientModal = useCallback(() => {
    setShowAddNewClientModal(false)
  }, [])

  const closeSuccessModal = useCallback(() => {
    track({ event: mixpanelEvents.ADD_NEW_CLIENT_CLOSE_SUCCESS_MODAL })
    setShowSuccessModal(false)
  }, [])

  return {
    allCustomers,
    showAddNewClientModal,
    showSuccessModal,
    addedClientFirstName,
    selectedCompany,
    submissionError,
    isAddingNewClient,
    careTypeOptions,
    isFetchingPrograms,
    addNewClient,
    openAddClientModal,
    openTransitionModal,
    closeAddClientModal,
    closeSuccessModal,
    setSelectedCompany,
    setAddedClientFirstName,
    setShowSuccessModal,
    setSubmissionError,
    setToastContent: (content: Parameters<typeof setToastContent>[0]) => dispatch(setToastContent(content)),
  }
}
